import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classnames from 'classnames';
import createMentionPlugin, { defaultSuggestionsFilter } from 'draft-js-mention-plugin';
import Editor from 'draft-js-plugins-editor';
import getRangesForDraftEntity from 'draft-js/lib/getRangesForDraftEntity';
import {
  Entity,
  EditorState,
  SelectionState,
  Modifier,
  convertFromRaw,
  convertFromHTML,
  ContentState,
} from 'draft-js';

import { pills as Pills, Icon, buttonsV2 as Buttons } from '@breezeway/brzw-components';
import {
  CLASS_NAME,
  creatBlock,
  createEntity,
  formatTokens,
  handleKeyCommand,
  messagesTokens,
  removeCharacterByRange,
  SECOND_LIST_CLASS_NAME,
  tokensForReadOnly,
  WORKSPACE_CLASS_NAME,
} from './helpers';
import TokenEntry from './token_entry';

import './token_textarea.scss';

const { BUTTON_THEMES } = Buttons.constants;

const OPENED_TYPES = {
  MENTION: 'mention',
  NONE: 'none',
  TOGGLE: 'toggle',
};

class TokenTextarea extends Component {
  constructor(props) {
    super(props);
    this.mentionPlugin = this.createMentionPlugin();

    // https://codesandbox.io/s/mqvp7mkkrx - editor for creating list with initial value with tokens
    this.state = {
      editorState: EditorState.createEmpty(),
      openedType: OPENED_TYPES.NONE,
      mentionIsOpened: false,
      suggestions: messagesTokens(props.tokens),
    };

    this.close = this.close.bind(this);
    this.createMentionPlugin = this.createMentionPlugin.bind(this);
    this.createToken = this.createToken.bind(this);
    this.deleteRange = this.deleteRange.bind(this);
    this.deleteToken = this.deleteToken.bind(this);
    this.finishEditing = this.finishEditing.bind(this);
    this.formatStringToMessage = this.formatStringToMessage.bind(this);
    this.getFormattedMessage = this.getFormattedMessage.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.onCancel = this.onCancel.bind(this);
    this.onCloseMention = this.onCloseMention.bind(this);
    this.onOpenMention = this.onOpenMention.bind(this);
    this.onSearchChange = this.onSearchChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onWorkspaceMouseDown = this.onWorkspaceMouseDown.bind(this);
    this.renderWorkspaceBar = this.renderWorkspaceBar.bind(this);
    this.renderWorkspaceButton = this.renderWorkspaceButton.bind(this);
    this.setFormattedMessage = this.setFormattedMessage.bind(this);
    this.setTokens = this.setTokens.bind(this);
    this.toggle = this.toggle.bind(this);

    this.setTokens(props.tokens);

    this.getFormattedInformationWithDebounce = _.debounce(this.getFormattedMessage, 200);

    this.DONT_RUN = false;

    this.tokenTextArea = React.createRef();
    this.listRef = React.createRef();
  }

  componentWillMount() {
    this.formatStringToMessage(this.props.initialValue);
    document.addEventListener('mousedown', this.handleClick, false);
  }

  componentDidMount() {
    this.setFormattedMessage();
  }

  componentDidUpdate(prevProps) {
    const { tokens, initialValue, readOnly } = this.props;
    const tokensAreDifferent = _.size(prevProps.tokens) !== _.size(tokens);
    const initialValuesAreDifferent = !_.isEqual(prevProps.initialValue, initialValue);

    if (tokensAreDifferent) {
      this.setTokens(tokens);
    }

    if (initialValuesAreDifferent || tokensAreDifferent) {
      this.setFormattedMessage();
      this.formatStringToMessage(initialValue);
      if (readOnly) {
        this.setTokens(tokensForReadOnly(initialValue));
      }
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClick, false);
  }

  onWorkspaceMouseDown(event) {
    _.invoke(event, 'preventDefault');
    _.invoke(event, 'stopPropagation');

    if (!this.state.dropdownIsOpen) {
      const scrollTop = _.get(this.listRef, 'current.scrollTop');
      if (scrollTop) {
        this.listRef.current.scrollTop = 0;
      }
    }
  }

  onCancel() {
    _.invoke(this.props, 'onCancel');
    this.formatStringToMessage(this.props.initialValue);
    this.finishEditing();
  }

  onSubmit() {
    const { formattedMessage } = this.state;

    _.invoke(this.props, 'onSubmit', formattedMessage);
    this.finishEditing();
  }

  onOpenMention() {
    this.setState({
      dropdownIsOpen: false,
      mentionIsOpened: true,
      openedType: OPENED_TYPES.MENTION,
    });
  }

  onCloseMention() {
    this.setState({ mentionIsOpened: false });
  }

  setTokens(tokens) {
    this.tokens = formatTokens(tokens);
    this.formattedTokens = messagesTokens(tokens);
  }

  setFormattedMessage(value = this.props.initialValue) {
    this.setState({ formattedMessage: value });

    if (!_.isEqual(value, this.props.initialValue)) {
      _.invoke(this.props, 'onChange', value);
    }
  }

  getFormattedMessage() {
    const { editorState } = this.state;
    const list = {};
    const currentContent = editorState.getCurrentContent();

    _.each(
      currentContent
        .getBlockMap()
        .toList()
        .toJS(),
      (row, key) => {
        list[key] = [];
        const rowData = editorState.toJS().treeMap[row.key];
        const textBlock = currentContent.getBlockForKey(row.key).text;

        _.each(rowData, t => {
          if (!_.isEmpty(t.decoratorKey)) {
            const entityKey = currentContent
              .getBlockForKey(row.key)
              .getCharacterList()
              .get(t.start)
              .getEntity();

            if (entityKey) {
              const codePerKey = _.get(
                currentContent.getEntity(entityKey).getData(),
                'mention.code',
              );
              if (codePerKey) {
                list[key].push(codePerKey);
              }
            }
          } else {
            list[key].push(_.join(_.slice(textBlock, t.start, t.end), ''));
          }
        });
      },
    );

    let message = '';
    _.each(list, block => {
      message += _.join(block, '');
    });

    this.setFormattedMessage(message);
    _.invoke(this.props, 'onChange', message);
  }

  createToken = mention => {
    const editorState = this.createTokenWithSelectedRange();
    const contentState = editorState.getCurrentContent();

    const newEntity = Entity.create('[mention', 'IMMUTABLE', { mention });
    const selectionState = editorState.getSelection();
    const offsetStart = selectionState.getStartOffset();
    const selectionKey = selectionState.getAnchorKey();

    const newSelectionStatePosition = new SelectionState({
      anchorKey: selectionKey,
      anchorOffset: offsetStart,
      focusKey: selectionState.getFocusKey(),
      focusOffset: offsetStart,
      hasFocus: selectionState.getHasFocus(),
      isBackward: selectionState.getIsBackward(),
    });

    const newContentState = Modifier.insertText(
      contentState,
      newSelectionStatePosition,
      mention.name,
      null,
      newEntity,
    );
    const stateWithMention = EditorState.push(editorState, newContentState, 'insert-characters'); // adding text with token

    const newEditorStateSelection = stateWithMention.getSelection();
    const newEditorStateContentState = stateWithMention.getCurrentContent();
    const newEditorStateWithFocus = newEditorStateSelection.merge({
      hasFocus: true,
      focusOffset: newEditorStateSelection.getFocusOffset(),
    });
    const newContentStateWithEmptySpace = Modifier.insertText(
      newEditorStateContentState,
      newEditorStateWithFocus,
      ' ',
    );
    const stateWithMentionAndSpace = EditorState.push(
      stateWithMention,
      newContentStateWithEmptySpace,
      'insert-characters',
    ); // adding empty space and set focus
    this.onChange(stateWithMentionAndSpace, () => {
      this.editor.focus();
      this.close();
    });
    this.close();
  };

  onChange = (editorState, callback) => {
    if (!this.DONT_RUN) {
      this.setState(
        {
          editorState,
        },
        () => {
          _.invoke(callback);
          this.getFormattedInformationWithDebounce();
        },
      );
    }

    if (this.DONT_RUN) {
      this.DONT_RUN = false;
    }
  };

  onSearchChange = ({ value }) => {
    const mentions = this.formattedTokens;
    this.setState({
      suggestions: _.isEmpty(value) ? mentions : defaultSuggestionsFilter(value, mentions),
    });
  };

  focus = () => {
    const { isFocused, dropdownIsOpen } = this.state;

    if (!isFocused) this.setState({ isFocused: true });
    this.editor.focus();
    _.invoke(this.props, 'onActiveChange', true);
    _.invoke(this.props, 'onFocus');

    if (!dropdownIsOpen) {
      const scrollTop = _.get(this.listRef, 'current.scrollTop');
      if (scrollTop) {
        this.listRef.current.scrollTop = 0;
      }
    }
  };

  blur = () => {
    _.invoke(this.props, 'onActiveChange', false);
    _.invoke(this.props, 'onBlur');
  };

  createTokenWithSelectedRange() {
    const { editorState } = this.state;

    const selectionState = editorState.getSelection();
    const offsetStart = selectionState.getStartOffset();
    const offsetEnd = selectionState.getEndOffset();
    const selectionKey = selectionState.getAnchorKey();

    const selectedFewLetters = offsetEnd - offsetStart > 0;

    if (selectedFewLetters) {
      return removeCharacterByRange(editorState, selectionKey, offsetStart, offsetEnd, false);
    }
    return editorState;
  }

  deleteToken(event, mentionProps) {
    _.invoke(event, 'stopPropagation');

    const { readOnly } = this.props;

    if (!readOnly) {
      const { editorState } = this.state;
      const contentState = editorState.getCurrentContent();
      const selectionState = editorState.getSelection();
      let block = _.get(mentionProps, ['children', 0, 'props', 'block']);

      let getBlock = getRangesForDraftEntity(block, mentionProps.entityKey);

      if (!getBlock) {
        const selectionKey = selectionState.getAnchorKey();
        block = contentState.getBlockForKey(selectionKey);
        getBlock = getRangesForDraftEntity(block, mentionProps.entityKey);
      }

      if (_.isArray(getBlock)) getBlock = _.first(getBlock);

      this.deleteRange(block, getBlock);
    }
  }

  deleteRange(block, getBlock) {
    const { editorState } = this.state;
    const contentState = editorState.getCurrentContent();

    const blockKey = block.getKey();

    const entitySelection = new SelectionState({
      anchorKey: blockKey,
      anchorOffset: getBlock.start,
      focusKey: blockKey,
      focusOffset: getBlock.end,
      hasFocus: true,
      isBackward: false,
    });

    const removingRange = Modifier.removeRange(contentState, entitySelection, 'backward');
    const stateWithoutRange = EditorState.push(editorState, removingRange, 'remove-range');
    this.onChange(stateWithoutRange);
  }

  formatStringToMessage(message) {
    const regex = /[[%%]+([a-z_0-9]*)[%%]+/;
    let newMessage = _.clone(message);

    let keyBlock = -1;
    let blocks = [];
    const entityMap = {};
    const randomKey =
      Math.random()
        .toString(36)
        .substring(2, 2) +
      Math.random()
        .toString(36)
        .substring(2, 10);
    const mentions = this.formattedTokens;

    const findMatches = (reg, str, matches = []) => {
      const result = reg.exec(str);
      if (result) {
        const mention = _.find(mentions, { code: result[0] });
        keyBlock += 1;
        const findIndexOfBlock = _.findIndex(blocks, { key: randomKey });

        if (mention) {
          if (_.isEmpty(blocks) || findIndexOfBlock === -1) {
            const block = creatBlock(randomKey, mention.name, result.index, keyBlock);
            blocks.push(block);
          } else {
            const newBlock = _.cloneDeep(blocks);

            newMessage = _.join(
              [
                ..._.slice(str, 0, result.index),
                mention.name,
                ..._.slice(str, result.index + result[0].length),
              ],
              '',
            );

            newBlock[findIndexOfBlock] = {
              ...newBlock[findIndexOfBlock],
              text: newMessage,
              entityRanges: [
                ...newBlock[findIndexOfBlock].entityRanges,
                {
                  offset: result.index,
                  length: mention.name.length,
                  key: keyBlock,
                },
              ],
            };
            blocks = newBlock;
          }
          entityMap[keyBlock] = createEntity(keyBlock, mention);

          if (matches.push(result)) findMatches(regex, newMessage, matches);
        }
      }

      return matches;
    };

    let editorState;
    let newEditorState;
    const getMatches = findMatches(regex, newMessage);

    if (_.isEmpty(getMatches)) {
      const blocksFromHTML = convertFromHTML(message);
      editorState = ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks,
        blocksFromHTML.entityMap,
      );
      newEditorState = EditorState.push(this.state.editorState, editorState, 'insert-characters');
    } else {
      editorState = convertFromRaw({
        blocks,
        entityMap,
      });
      newEditorState = EditorState.createWithContent(editorState);
    }

    this.onChange(newEditorState);
  }

  createMentionPlugin() {
    const { readOnly } = this.props;
    const mentions = this.formattedTokens;
    return createMentionPlugin({
      mentions,
      entityMutability: 'IMMUTABLE',
      theme: {
        mentionSuggestions: SECOND_LIST_CLASS_NAME,
        mentionSuggestionsEntry: `${SECOND_LIST_CLASS_NAME}__entry`,
        mentionSuggestionsEntryText: `${SECOND_LIST_CLASS_NAME}__text`,
        mentionSuggestionsEntryFocused: `${SECOND_LIST_CLASS_NAME}__entry--focused`,
      },
      mentionPrefix: '',
      mentionTrigger: '[',
      supportWhitespace: false,
      mentionComponent: mentionProps => (
        <Pills.Pill
          readOnly={readOnly}
          className={`${CLASS_NAME}__token`}
          onClick={event => this.deleteToken(event, mentionProps)}
          text={mentionProps.children}
          theme="secondary"
        />
      ),
    });
  }

  toggle() {
    this.setState(prevState => {
      if (!prevState.dropdownIsOpen) {
        return {
          dropdownIsOpen: !prevState.dropdownIsOpen,
          isFocused: true,
          openedType: OPENED_TYPES.TOGGLE,
        };
      }
      return { dropdownIsOpen: !prevState.dropdownIsOpen, isFocused: true };
    });
  }

  close() {
    this.setState({ dropdownIsOpen: false });
  }

  finishEditing() {
    this.setState({ isFocused: false, dropdownIsOpen: false });
  }

  handleClick(e) {
    const shouldDisableField = !this.tokenTextArea.current.contains(e.target);
    if (shouldDisableField) this.finishEditing();
  }

  handleKeyCommand(command, b, c) {
    const { disableTokens } = this.props;
    if (!disableTokens && command === 'backspace') {
      const editorState = c.getEditorState();
      const getSelection = editorState.getSelection();
      const currentContent = editorState.getCurrentContent();
      const offset = getSelection.getEndOffset();
      const selectionKey = getSelection.getAnchorKey();
      const block = currentContent.getBlockForKey(selectionKey);
      const entityKey = block.getEntityAt(offset - 2);

      if (entityKey && _.get(currentContent.getPlainText(), [offset - 1], '') === ' ') {
        let getBlock = getRangesForDraftEntity(block, entityKey);

        if (_.isArray(getBlock)) getBlock = _.first(getBlock);

        this.deleteRange(block, getBlock);

        this.DONT_RUN = true;
      }
    }
  }

  renderWorkspaceButton(mentionsByGroup, groupName) {
    return _.map(mentionsByGroup, (token, index) => (
      <li key={`${index}-$${groupName}`}>
        <button
          type="button"
          className={`${WORKSPACE_CLASS_NAME}__list-button`}
          onClick={() => this.createToken(token)}
        >
          {token.name}
        </button>
      </li>
    ));
  }

  renderWorkspaceBar() {
    const { dropdownIsOpen } = this.state;

    return (
      <div
        role="presentation"
        className={classnames(WORKSPACE_CLASS_NAME, { '-is-open': dropdownIsOpen })}
        onMouseDown={this.onWorkspaceMouseDown}
      >
        <div className={`${WORKSPACE_CLASS_NAME}__title`} onClick={this.toggle} role="presentation">
          <Icon iconName="token" className={`${WORKSPACE_CLASS_NAME}__title-icon`} />
          Tokens
        </div>
        <ul className={`${WORKSPACE_CLASS_NAME}__list`} ref={this.listRef}>
          {_.map(this.tokens, group => {
            if (!_.isEmpty(group.tokens)) {
              return (
                <Fragment key={group.name}>
                  <div className={`${WORKSPACE_CLASS_NAME}__list-title`}>{group.name}</div>
                  {this.renderWorkspaceButton(group.tokens, group.name)}
                </Fragment>
              );
            }
            return null;
          })}
        </ul>
      </div>
    );
  }

  render() {
    const {
      dropdownIsOpen,
      editorState,
      formattedMessage,
      isFocused,
      mentionIsOpened,
      openedType,
      suggestions,
    } = this.state;
    const {
      className,
      disableTokens,
      initialValue,
      placeholderText,
      readOnly,
      withButtons,
    } = this.props;
    const { MentionSuggestions } = this.mentionPlugin;
    const plugins = [this.mentionPlugin];
    const showButtons = !_.isEqual(initialValue, formattedMessage);

    return (
      <div ref={this.tokenTextArea}>
        <div
          className={classnames(`${CLASS_NAME}-wrapper`, className, {
            '-is-opened-mention': mentionIsOpened && openedType === OPENED_TYPES.MENTION,
          })}
        >
          <div
            className={classnames('editor', CLASS_NAME, {
              '-focused': isFocused || dropdownIsOpen,
              '-disabled': readOnly,
            })}
            onClick={!readOnly ? this.focus : undefined}
            role="presentation"
          >
            <Editor
              className={`${CLASS_NAME}__field`}
              editorState={editorState}
              handleKeyCommand={handleKeyCommand}
              onBlur={this.blur}
              onChange={this.onChange}
              onFocus={this.focus}
              placeholder={placeholderText}
              plugins={disableTokens ? [] : plugins}
              ref={element => {
                this.editor = element;
              }}
              readOnly={readOnly}
            />

            {!disableTokens && (
              <MentionSuggestions
                entryComponent={params => <TokenEntry {...params} tokens={this.tokens} />}
                onClose={this.onCloseMention}
                onOpen={this.onOpenMention}
                onSearchChange={this.onSearchChange}
                suggestions={suggestions}
              />
            )}
          </div>
          {!readOnly && !disableTokens && this.renderWorkspaceBar()}
        </div>

        {withButtons && showButtons && (
          <div className={`${CLASS_NAME}__button-wrapper`}>
            <Buttons.TextButton onClick={this.onSubmit}>Save</Buttons.TextButton>
            <Buttons.TextButton
              className={`${CLASS_NAME}__button-cancel`}
              onClick={this.onCancel}
              theme={BUTTON_THEMES.SUBTLE}
            >
              Cancel
            </Buttons.TextButton>
          </div>
        )}
      </div>
    );
  }
}

TokenTextarea.propTypes = {
  className: PropTypes.string,
  disableTokens: PropTypes.bool,
  initialValue: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  placeholderText: PropTypes.string,
  readOnly: PropTypes.bool,
  tokens: PropTypes.arrayOf(PropTypes.shape()),
  withButtons: PropTypes.bool,
};

TokenTextarea.defaultProps = {
  className: '',
  disableTokens: false,
  initialValue: '',
  readOnly: false,
  withButtons: true,
};

export default TokenTextarea;
